/**
 * MatchPronoModal — Pronostic du score exact AVANT le coup d'envoi.
 *
 * Props :
 *  - match : { id, home_team, away_team, home_logo, away_logo, date_iso, time }
 *  - kickoffIso : ISO datetime du coup d'envoi (calculé depuis date_iso + time)
 *  - onClose : fermer la modal
 *
 * Utilise localStorage `iphb_voter_key` (UUID) pour identifier le supporter.
 * Backend : POST /match-live/prono { match_id, voter_key, voter_name, home_pred, away_pred, kickoff_iso }
 *           GET  /match-live/prono/mine?match_id&voter_key
 *           GET  /match-live/games-config (lot/sponsor)
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Target, Check, LogIn } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCustomerAuth } from "../../lib/useCustomerAuth";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#0D0220";

function authClient() {
  const token = localStorage.getItem("msc_customer_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export default function MatchPronoModal({ match, kickoffIso, onClose }) {
  const matchId = String(match?.id || "");
  const { isAuthenticated, customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({});

  // Verrouillage : si kickoff passé
  const locked = useMemo(() => {
    if (!kickoffIso) return false;
    try {
      const ko = new Date(kickoffIso).getTime();
      return !isNaN(ko) && Date.now() >= ko;
    } catch { return false; }
  }, [kickoffIso]);

  // Charger pronostic existant + config (lot/sponsor)
  useEffect(() => {
    if (!matchId || !isAuthenticated) return;
    api.get(`/match-live/prono/mine?match_id=${matchId}`, authClient())
      .then((r) => {
        const p = r.data?.prono;
        if (p) {
          setMine(p);
          setHome(String(p.home_pred));
          setAway(String(p.away_pred));
        }
      }).catch(() => {});
    api.get("/match-live/games-config")
      .then((r) => setCfg(r.data?.config || {}))
      .catch(() => {});
  }, [matchId, isAuthenticated]);

  const goLogin = () => {
    onClose?.();
    navigate(`/sport/mon-espace?next=prono&match=${encodeURIComponent(matchId)}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      goLogin();
      return;
    }
    if (locked) {
      toast.error("Le match a déjà commencé — pronostic verrouillé");
      return;
    }
    const hp = parseInt(home, 10);
    const ap = parseInt(away, 10);
    if (isNaN(hp) || isNaN(ap) || hp < 0 || ap < 0 || hp > 99 || ap > 99) {
      toast.error("Score invalide (0-99)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/match-live/prono", {
        match_id: matchId,
        home_pred: hp,
        away_pred: ap,
        kickoff_iso: kickoffIso,
      }, authClient());
      setMine({ home_pred: hp, away_pred: ap, submitted_at: new Date().toISOString() });
      toast.success(`Pronostic ${hp}-${ap} enregistré !`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const homeName = match?.home_team || "Domicile";
  const awayName = match?.away_team || "Extérieur";

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: "rgba(5,0,16,0.85)", backdropFilter: "blur(12px)", isolation: "isolate" }}
        onClick={onClose}
        data-testid="match-prono-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl text-white"
          style={{
            background: `radial-gradient(circle at top, ${PRIMARY}, ${BG_DEEP} 70%)`,
            border: `1px solid ${SECONDARY}55`,
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="match-prono-modal"
        >
          <button
            type="button" onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 transition z-10"
            data-testid="match-prono-modal-close"
          >
            <X size={18}/>
          </button>

          <div className="p-6 pb-5">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: `${SECONDARY}30`, border: `1.5px solid ${SECONDARY}88` }}>
                <Target size={18} className="text-pink-300" strokeWidth={2}/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-pink-300 font-bold">Jeu Pronostic</div>
                <div className="font-black uppercase text-white text-lg leading-tight" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif" }}>
                  Devinez le score exact
                </div>
              </div>
            </div>

            {/* Match teaser */}
            <div className="mt-4 grid grid-cols-[1fr,auto,1fr] items-center gap-3 px-2">
              <div className="flex flex-col items-center text-center">
                {match?.home_logo && <img src={match.home_logo} alt={homeName} className="h-14 w-14 object-contain mb-1.5" onError={(e) => { e.currentTarget.style.display = "none"; }}/>}
                <div className="text-white text-xs font-bold uppercase truncate w-full">{homeName}</div>
              </div>
              <div className="font-blackhawk italic text-3xl" style={{ color: SECONDARY }}>VS</div>
              <div className="flex flex-col items-center text-center">
                {match?.away_logo && <img src={match.away_logo} alt={awayName} className="h-14 w-14 object-contain mb-1.5" onError={(e) => { e.currentTarget.style.display = "none"; }}/>}
                <div className="text-white text-xs font-bold uppercase truncate w-full">{awayName}</div>
              </div>
            </div>

            {/* Form / mine / login required */}
            {!isAuthenticated ? (
              <div className="mt-5 p-5 rounded-xl border border-pink-400/40 bg-pink-500/5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center"
                  style={{ background: `${SECONDARY}25`, border: `1.5px solid ${SECONDARY}80` }}>
                  <LogIn size={22} className="text-pink-300"/>
                </div>
                <div className="text-white font-bold text-base mb-1">Connexion supporter requise</div>
                <div className="text-white/65 text-sm mb-4 max-w-xs mx-auto">
                  Créez un compte gratuit pour soumettre votre pronostic, suivre vos matchs et tenter de gagner des lots toute la saison.
                </div>
                <button type="button" onClick={goLogin}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase tracking-wider text-white text-sm shadow-lg active:scale-95 transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 10px 28px ${SECONDARY}55` }}
                  data-testid="match-prono-login-cta">
                  <LogIn size={14}/> Me connecter / créer un compte
                </button>
              </div>
            ) : locked ? (
              <div className="mt-5 p-4 rounded-xl border border-yellow-400/30 bg-yellow-400/5 text-center">
                <div className="text-yellow-300 text-xs uppercase tracking-wider font-bold mb-1">⚠ Pronostic verrouillé</div>
                <div className="text-white/75 text-sm">Le coup d'envoi est passé — les pronostics sont fermés.</div>
                {mine && (
                  <div className="mt-3 text-white">
                    Votre prono : <span className="font-black text-2xl tabular-nums" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{mine.home_pred} - {mine.away_pred}</span>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="match-prono-form">
                <div className="text-[11px] text-white/65 text-center -mt-1 mb-1">
                  Connecté en tant que <span className="text-pink-300 font-bold">{customer?.first_name || customer?.email}</span>
                </div>
                {mine && (
                  <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                    <Check size={16} className="text-green-400"/>
                    <div className="text-sm">
                      <span className="text-green-300 font-bold">Prono enregistré : </span>
                      <span className="text-white font-black tabular-nums" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{mine.home_pred} - {mine.away_pred}</span>
                      <span className="text-white/50 text-xs ml-1">· modifiable</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/60 mb-1.5 font-bold">{homeName.slice(0, 14)}</label>
                    <input type="number" min="0" max="99" value={home} onChange={(e) => setHome(e.target.value)}
                      className="w-full px-3 py-3 rounded-xl bg-black/40 border border-white/15 text-white text-3xl text-center font-black tabular-nums focus:outline-none focus:border-pink-400"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                      data-testid="match-prono-home"/>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/60 mb-1.5 font-bold">{awayName.slice(0, 14)}</label>
                    <input type="number" min="0" max="99" value={away} onChange={(e) => setAway(e.target.value)}
                      className="w-full px-3 py-3 rounded-xl bg-black/40 border border-white/15 text-white text-3xl text-center font-black tabular-nums focus:outline-none focus:border-pink-400"
                      style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                      data-testid="match-prono-away"/>
                  </div>
                </div>
                <button type="submit" disabled={busy || !home || !away}
                  className="w-full py-3 rounded-xl font-black uppercase tracking-wider text-white disabled:opacity-50 transition active:scale-[0.99]"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 10px 28px ${SECONDARY}55` }}
                  data-testid="match-prono-submit">
                  {busy ? "Envoi…" : mine ? "Modifier mon prono" : "Valider mon pronostic"}
                </button>
                <div className="text-[11px] text-white/50 text-center">
                  1 prono par supporter · Verrouillé au coup d'envoi
                </div>
              </form>
            )}

            {/* Sponsor + lot */}
            {(cfg.prono_prize || cfg.prono_sponsor_name) && (
              <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
                {cfg.prono_sponsor_logo && (
                  <img src={cfg.prono_sponsor_logo} alt={cfg.prono_sponsor_name} className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1.5 flex-shrink-0"/>
                )}
                <div className="flex-1 min-w-0">
                  {cfg.prono_prize && <div className="text-pink-300 text-[11px] uppercase tracking-wider font-bold">🏆 Lot · {cfg.prono_prize}</div>}
                  {cfg.prono_sponsor_name && <div className="text-white/75 text-xs mt-0.5">offert par {cfg.prono_sponsor_name}</div>}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
